
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { LogOut, Bell, Menu, X } from "lucide-react";
import logoCsi from "../../assets/logo/LogoCSI.png";

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia("(max-width: 768px)").matches
  );
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");
    const updateScreen = () => setIsMobile(media.matches);

    media.addEventListener("change", updateScreen);
    return () => media.removeEventListener("change", updateScreen);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate("/login");
  };

  const isAuthPage = location.pathname === "/login";

  const isDashboard =
    location.pathname.includes("/dashboard") ||
    location.pathname.includes("/rh/") ||
    location.pathname.includes("/employee/") ||
    location.pathname.includes("/manager/");

  const getProfilePath = (role) => {
    switch (String(role || "").toLowerCase()) {
      case "rh": return "/rh/profile";
      case "employee": return "/employee/profile";
      case "manager": return "/manager/profile";
      default: return "/profile";
    }
  };

  const getRoleLabel = (role) => {
    switch (String(role || "").toLowerCase()) {
      case "employee":
        return { label: "Employé", className: "badge-employee" };
      case "manager":
        return { label: "Manager", className: "badge-manager" };
      case "rh":
        return { label: "RH", className: "badge-rh" };
      case "admin":
        return { label: "Administrateur", className: "badge-admin" };
      default:
        return { label: role || "Utilisateur", className: "" };
    }
  };

  const roleInfo = user ? getRoleLabel(user.role) : null;
  const profilePath = user ? getProfilePath(user.role) : "/login";

  const notificationPath = {
    rh: "/rh/notifications",
    admin: "/rh/notifications",
    employee: "/employee/notifications",
    manager: "/manager/notifications",
  }[String(user?.role || "").toLowerCase()];

  const buttonStyle = {
    minHeight: 42,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: isMobile ? "8px" : "10px 14px",
    borderRadius: 9,
    cursor: "pointer",
    fontSize: 14,
    flexShrink: 0,
  };

  return (
    <>
      <nav
        className={`navbar-global ${isDashboard ? "navbar-dashboard" : ""}`}
        style={{
          width: "100%",
          boxSizing: "border-box",
          position: "relative",
          zIndex: 1100,
        }}
      >
        <div
          className="navbar-container"
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: isMobile ? 8 : 16,
            padding: isMobile ? "10px 12px" : "12px 24px",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Link
            to="/"
            className="brand-logo"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              textDecoration: "none",
              minWidth: 0,
            }}
          >
            <img
              src={logoCsi}
              alt="CSI Logo"
              style={{
                height: isMobile ? 30 : 40,
                width: "auto",
                objectFit: "contain",
              }}
            />
            <span style={{ fontSize: isMobile ? 15 : 18, fontWeight: 700 }}>
              Smart-HR
            </span>
          </Link>

          {user && isDashboard && isMobile && (
            <button
              type="button"
              style={{
                ...buttonStyle,
                width: 42,
                padding: 0,
                background: "transparent",
                border: "1px solid var(--border)",
                color: "var(--text-main)",
                order: -1,
              }}
              aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          )}

          {!user && !isAuthPage && !isMobile && (
            <div
              className="navbar-links"
              style={{ display: "flex", gap: 20, marginLeft: "auto" }}
            >
              <a href="#home">Accueil</a>
              <a href="#modules">Modules</a>
              <a href="#workflow">Workflow</a>
              <a href="#ai">IA</a>
            </div>
          )}

          <div
            className="navbar-actions"
            style={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 5 : 10,
              marginLeft: "auto",
              minWidth: 0,
            }}
          >
            {user ? (
              <>
                {notificationPath && (
                  <button
                    type="button"
                    className="nav-icon-btn"
                    style={buttonStyle}
                    aria-label="Notifications"
                    onClick={() => navigate(notificationPath)}
                  >
                    <Bell size={20} />
                    <span className="notification-dot" />
                  </button>
                )}

                <button
                  type="button"
                  className="nav-profile-button"
                  style={{
                    ...buttonStyle,
                    border: "none",
                    background: "transparent",
                    color: "inherit",
                  }}
                  onClick={() => navigate(profilePath)}
                  title="Ouvrir mon profil"
                >
                  <div
                    className="nav-avatar-default"
                    style={{ flexShrink: 0 }}
                  >
                    {user?.name
                      ? user.name.charAt(0).toUpperCase()
                      : "U"}
                  </div>

                  {!isMobile && (
                    <div
                      className="nav-profile-text"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <span className="nav-username">
                        {user?.name || "Utilisateur"}
                      </span>
                      <span className={`nav-role-badge ${roleInfo.className}`}>
                        {roleInfo.label}
                      </span>
                    </div>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="nav-btn nav-btn-logout"
                  style={buttonStyle}
                  title="Se déconnecter"
                >
                  <LogOut size={17} />
                  {!isMobile && <span>Déconnexion</span>}
                </button>
              </>
            ) : (
              !isAuthPage && (
                <>
                  <Link
                    to="/login"
                    className="nav-btn nav-btn-outline"
                    style={buttonStyle}
                  >
                    Connexion
                  </Link>
                  {!isMobile && (
                    <a
                      href="#home"
                      className="nav-btn nav-btn-primary"
                      style={buttonStyle}
                    >
                      Démo gratuite
                    </a>
                  )}
                </>
              )
            )}
          </div>
        </div>
      </nav>

      {/* Menu mobile autonome */}
      {user && isDashboard && isMobile && menuOpen && (
        <>
          <div
            onClick={() => setMenuOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1190,
              background: "rgba(0,0,0,0.4)",
            }}
          />

          <div
            style={{
              position: "fixed",
              zIndex: 1200,
              top: 0,
              bottom: 0,
              left: 0,
              width: "min(85vw, 320px)",
              overflowY: "auto",
              padding: "24px 16px",
              boxSizing: "border-box",
              background: "var(--bg-main)",
              boxShadow: "8px 0 24px rgba(0,0,0,0.15)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 20,
              }}
            >
              <strong>Menu Smart HR</strong>
              <button
                type="button"
                aria-label="Fermer le menu"
                onClick={() => setMenuOpen(false)}
                style={{ ...buttonStyle, border: "none", background: "transparent" }}
              >
                <X size={22} />
              </button>
            </div>

            {[
              ...(String(user.role || "").toLowerCase() === "employee"
                ? [
                    { label: "Vue globale", path: "/employee/dashboard" },
                    { label: "Demander congé", path: "/employee/leave-create" },
                    { label: "Mes demandes", path: "/employee/leave-history" },
                  ]
                : []),
              ...(String(user.role || "").toLowerCase() === "manager"
                ? [
                    { label: "Suivi équipe", path: "/manager/dashboard" },
                    { label: "Demandes à valider", path: "/manager/leaves" },
                    { label: "Mon espace Employé", path: "/employee/dashboard" },
                  ]
                : []),
              ...(["rh", "admin"].includes(
                String(user.role || "").toLowerCase()
              )
                ? [
                    { label: "Stats globales", path: "/rh/dashboard" },
                    { label: "Collaborateurs", path: "/rh/employees" },
                    { label: "Calendrier RH", path: "/rh/calendar" },
                    { label: "Jours fériés", path: "/rh/holidays" },
                    { label: "Validations RH", path: "/rh/leaves" },
                    { label: "Historique congés", path: "/rh/leaves/history" },
                    { label: "Articles", path: "/rh/articles" },
                    { label: "Contrats", path: "/rh/contracts" },
                    { label: "Notifications", path: "/rh/notifications" },
                    { label: "Journal d'audit", path: "/rh/audit" },
                    { label: "Offres d'emploi", path: "/rh/job-offers" },
                    { label: "Candidatures", path: "/rh/applications" },
                    { label: "Préparation entretiens", path: "/rh/interview-preparation" },
                    { label: "Recrutement IA", path: "/rh/ai-dashboard" },
                    { label: "Ranking détaillé", path: "/rh/cv-ranking" },
                    { label: "Assistant IA", path: "/rh/assistant" },
                    { label: "Mon espace Employé", path: "/employee/dashboard" },
                  ]
                : []),
              { label: "Profil", path: profilePath },
            ].map((item) => (
              <button
                key={`${item.label}-${item.path}`}
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  navigate(item.path);
                }}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "13px 10px",
                  marginBottom: 5,
                  textAlign: "left",
                  border: "none",
                  borderRadius: 8,
                  background:
                    location.pathname === item.path
                      ? "var(--primary-light)"
                      : "transparent",
                  color: "var(--text-main)",
                  cursor: "pointer",
                  fontSize: 15,
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;
